import { useEffect, useState } from 'react';
import type { InfoMessage } from '@goanna-im/shared';
import { visibleMessage } from '@goanna-im/shared';

/**
 * Upper information zone. Shows the current cue/message with an obvious kicker and
 * large body. Urgent messages are styled distinctly (never colour alone — they also
 * carry the URGENT kicker). Expiry is handled locally so a message clears on time
 * even if the producer's clear never arrives.
 */
export function InfoZone({ message, heightPct }: { message: InfoMessage | null; heightPct: number }) {
  const [, force] = useState(0);

  // Re-render around the expiry moment so the message disappears on schedule.
  useEffect(() => {
    if (!message?.expiresAt) return;
    const delay = Math.max(0, message.expiresAt - Date.now()) + 30;
    const t = setTimeout(() => force((n) => n + 1), delay);
    return () => clearTimeout(t);
  }, [message]);

  const visible = visibleMessage(message, Date.now());
  const urgent = visible?.type === 'urgent';

  return (
    <div
      className={`info-zone ${urgent ? 'urgent' : ''} ${visible ? '' : 'empty'}`}
      style={{ height: `${heightPct}%` }}
      aria-live="assertive"
    >
      {visible ? (
        <>
          <div className="kicker">{visible.heading || visible.type}</div>
          <div className="body">{visible.body}</div>
        </>
      ) : (
        <div className="body">Ready</div>
      )}
    </div>
  );
}
